//@flow

import {useCallback, useEffect, useRef, useState} from 'react';

import {encodeAudioToWav} from '../audio';
import {colors} from '../../assets/styles/colors';

const getAudioWorkletModuleBlob = () => {
  const sourceCode = `
      class AudioRecorder extends AudioWorkletProcessor {
        static get parameterDescriptors () { // <1>
          return [
            {
              name: 'isRecording',
              defaultValue: 0,
              minValue: 0,
              maxValue: 1,
            },
          ]
        }
      
        process (inputs, outputs, parameters) {
          const buffer = []
          const channel = 0
      
          for (let t = 0; t < inputs[0][channel].length; t += 1) {
            if (parameters.isRecording[0] === 1) {
              buffer.push(inputs[0][channel][t])
            }
          }
      
          if (buffer.length >= 1) {
            this.port.postMessage({buffer})
          }
      
          return true
        }
      }
  
      registerProcessor('audio-recorder', AudioRecorder)
    `;

  const blob = new Blob([sourceCode], {type: 'text/javascript'});

  return blob;
};

type UseAudioRecorderParams = {
  onError: (error: Error) => void,
  autoStart?: boolean,
  onStarted?: () => void,
  onStopped?: () => void,
};

type RecorderState = 'recording' | 'paused' | 'inactive' | 'stopped';

// source: https://gist.github.com/tatsuyasusukida/b6daa0cd09bba2fbbf6289c58777eeca#file-audio-recorder-js
export function useAudioRecorder(params: UseAudioRecorderParams) {
  const {onError, autoStart} = params;

  const [recorderState, setRecorderState] = useState<RecorderState>(
    autoStart ? 'recording' : 'inactive'
  );

  const [recodingResult, setRecodingResult] = useState<Blob | null>(null);

  const [elapsedSeconds, setElapsedSeconds] = useState(0);

  const audioContextRef = useRef<window.AudioContext | null>(null);
  const audioRecorderRef = useRef<window.AudioWorkletNode | null>(null);
  const buffersRef = useRef<Array<number>>([]);
  const audioTrackSettingsRef = useRef<MediaTrackSettings | null>(null);
  const elapsedIntervalRef = useRef<number | null>(null);

  const canvasRef = useRef<HTMLCanvasElement | null>(null);

  const analyserRef = useRef<AnalyserNode | null>(null);

  const animationFrameRef = useRef(null);

  const animate = useCallback(() => {
    const canvas = canvasRef.current;
    const analyser = analyserRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    if (!analyser) return;

    ctx.clearRect(0, 0, canvas.width, canvas.height);

    const data = new Uint8Array(analyser.frequencyBinCount);
    analyser.getByteFrequencyData(data);

    const time = Date.now() * 0.01;

    const centerY = canvas.height / 2;

    for (let i = 0; i < data.length; i++) {
      const value = data[i];
      const percent = value / 255;
      const maxBarHeight = canvas.height * percent;
      const barWidth = canvas.width / data.length;
      const barHeight =
        Math.sin(i * barWidth + time) * 0.5 * maxBarHeight + 0.5 * maxBarHeight;

      ctx.fillStyle = colors.primary;
      ctx.fillRect(i * barWidth, centerY, barWidth, barHeight);
      ctx.fillRect(i * barWidth, centerY, barWidth, -barHeight);
    }

    animationFrameRef.current = requestAnimationFrame(animate);
  }, []);

  const handleStart = useCallback(() => {
    setRecorderState('recording');

    animate();

    const audioContext = audioContextRef.current;
    const audioRecorder = audioRecorderRef.current;
    const buffers = buffersRef.current;

    if (!audioContext || !audioRecorder) return;

    const parameter = audioRecorder.parameters.get('isRecording');
    parameter.setValueAtTime(1, audioContext.currentTime);
    buffers.splice(0, buffers.length);

    elapsedIntervalRef.current = window.setInterval(() => {
      setElapsedSeconds(seconds => seconds + 1);
    }, 1000);
  }, [animate]);

  const handleStop = useCallback(() => {
    const audioContext = audioContextRef.current;
    const audioRecorder = audioRecorderRef.current;
    const buffers = buffersRef.current;
    const settings = audioTrackSettingsRef.current;

    if (!audioContext || !audioRecorder) return;
    const parameter = audioRecorder.parameters.get('isRecording');
    parameter.setValueAtTime(0, audioContext.currentTime);

    const blob = encodeAudioToWav(buffers, settings);
    setRecorderState('stopped');
    setRecodingResult(blob);

    if (elapsedIntervalRef.current) {
      window.clearInterval(elapsedIntervalRef.current);
      elapsedIntervalRef.current = null;
    }

    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
      animationFrameRef.current = null;
    }
  }, []);

  useEffect(() => {
    let audioWorkletModuleUrl = null;
    let stream = null;

    const main = async () => {
      stream = await window.navigator.mediaDevices.getUserMedia({
        vide: false,
        audio: true,
      });

      const [track] = stream.getAudioTracks();
      const settings = track.getSettings();

      audioTrackSettingsRef.current = settings;

      audioContextRef.current = new window.AudioContext();

      audioWorkletModuleUrl = URL.createObjectURL(getAudioWorkletModuleBlob());

      const audioContext = audioContextRef.current;
      if (!audioContext) return;

      await audioContext.audioWorklet.addModule(audioWorkletModuleUrl);

      const mediaStreamSource = audioContext.createMediaStreamSource(stream);
      audioRecorderRef.current = new window.AudioWorkletNode(
        audioContext,
        'audio-recorder'
      );
      const buffers = buffersRef.current;

      const audioRecorder = audioRecorderRef.current;

      audioRecorder.port.addEventListener('message', event => {
        buffers.push(event.data.buffer);
      });

      audioRecorder.port.start();
      mediaStreamSource.connect(audioRecorder);
      audioRecorder.connect(audioContext.destination);

      const analyser = audioContext.createAnalyser();

      analyserRef.current = analyser;

      mediaStreamSource.connect(analyser);
      analyser.connect(audioRecorder);

      if (autoStart) {
        handleStart();
      }
    };
    main().catch(error => {
      console.error(`error recording audio: ${error}`);
      onError(error);
    });

    return () => {
      if (audioWorkletModuleUrl) {
        URL.revokeObjectURL(audioWorkletModuleUrl);
      }

      if (stream) {
        stream.getTracks().forEach(track => track.stop());
      }
    };
  }, [autoStart, handleStart, onError]);

  return {
    recorderState,
    start: handleStart,
    stop: handleStop,
    recodingResult,
    elapsedSeconds,
    canvasRef,
  };
}
